import { useEffect, useState, type RefObject } from 'react';

/**
 * t272u: whether a scroll area has more content above or below its visible part, to show a soft fade at that edge.
 * A 2px tolerance absorbs sub-pixel scroll positions at the very top or bottom.
 */
export function scrollEdges({ scrollTop, scrollHeight, clientHeight }: { scrollTop: number; scrollHeight: number; clientHeight: number }) {
  return { top: scrollTop > 2, bottom: scrollHeight - clientHeight - scrollTop > 2 };
}

/** Live `scrollEdges` for an element: on scroll, on resize and when its content changes. */
export function useScrollEdges(ref: RefObject<HTMLElement | null>) {
  const [edges, setEdges] = useState({ top: false, bottom: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setEdges(prev => {
      const next = scrollEdges(el);
      return prev.top === next.top && prev.bottom === next.bottom ? prev : next;
    });
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(update) : null;
    mo?.observe(el, { childList: true, subtree: true });
    return () => { el.removeEventListener('scroll', update); ro?.disconnect(); mo?.disconnect(); };
  }, [ref]);
  return edges;
}
